import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, LogOut, Phone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["New", "Contacted", "Closed"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("adminToken") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("All");

  const loadLeads = useCallback(async (activeToken) => {
    try {
      const res = await fetch(`${API}/api/leads`, { headers: { Authorization: `Bearer ${activeToken}` } });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem("adminToken");
        setToken("");
        return;
      }
      setLeads(await res.json());
    } catch {
      setLeads([]);
    }
  }, []);

  useEffect(() => { if (token) loadLeads(token); }, [token, loadLeads]);

  const login = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      localStorage.setItem("adminToken", data.token);
      setToken(data.token);
    } catch {
      setError("Could not reach the server. Please try again.");
    }
  };

  const updateStatus = async (leadId, status) => {
    const res = await fetch(`${API}/api/leads/${leadId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.ok) loadLeads(token);
  };

  const logout = () => { localStorage.removeItem("adminToken"); setToken(""); setLeads(null); };

  if (!token) {
    return <div className="admin-shell" data-testid="admin-login-page">
      <form className="admin-login-card" onSubmit={login} data-testid="admin-login-form">
        <Link className="brand" to="/" data-testid="admin-brand-link"><img className="brand-logo" src="/logo.png" alt="Richcube Wealth Management" /></Link>
        <h1>Team sign in</h1>
        <p>Private access to website enquiries.</p>
        {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
        <label htmlFor="admin-email">Email</label>
        <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@richcube.in" />
        <label htmlFor="admin-password">Password</label>
        <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <button className="button button-gold" type="submit" data-testid="admin-login-button">Sign in <ArrowUpRight size={15} /></button>
      </form>
    </div>;
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: (leads || []).filter((l) => (l.status || "New") === s).length }), {});
  const visible = (leads || []).filter((l) => filter === "All" || (l.status || "New") === filter);

  return <div className="admin-shell admin-wide" data-testid="admin-dashboard">
    <header className="admin-topbar">
      <Link className="brand" to="/" data-testid="admin-brand-link"><img className="brand-logo" src="/logo.png" alt="Richcube Wealth Management" /></Link>
      <button className="admin-logout" onClick={logout} data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>
    </header>
    <div className="admin-head">
      <div><p className="eyebrow">ENQUIRIES</p><h1>Lead dashboard</h1></div>
      <button className="text-button" onClick={() => loadLeads(token)} data-testid="admin-refresh-button">Refresh <ArrowUpRight size={14} /></button>
    </div>
    <div className="admin-stats" data-testid="admin-stats">
      <div data-testid="admin-stat-total"><strong>{(leads || []).length}</strong><span>Total enquiries</span></div>
      {STATUSES.map((s) => <div key={s} data-testid={`admin-stat-${s.toLowerCase()}`}><strong>{counts[s]}</strong><span>{s}</span></div>)}
    </div>
    <div className="admin-filters">{["All", ...STATUSES].map((s) => <button key={s} className={filter === s ? "selected" : ""} onClick={() => setFilter(s)} data-testid={`admin-filter-${s.toLowerCase()}`}>{s}</button>)}</div>
    {leads === null ? <p className="admin-empty">Loading enquiries…</p> : visible.length === 0 ? <p className="admin-empty" data-testid="admin-empty-state">No enquiries here yet.</p> : (
      <div className="admin-lead-list">
        {visible.map((lead, index) => {
          const status = lead.status || "New";
          const digits = (lead.phone || "").replace(/\D/g, "");
          return <article className="admin-lead-card" key={lead.id} data-testid={`lead-row-${index}`}>
            <div className="admin-lead-main">
              <div className="admin-lead-title"><h3>{lead.name}</h3><span className={`status-pill status-${status.toLowerCase()}`} data-testid={`lead-status-${index}`}>{status}</span></div>
              <p className="admin-lead-meta">{lead.interest}{lead.location ? ` · ${lead.location}` : ""} · {new Date(lead.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
              {lead.message && <p className="admin-lead-message">“{lead.message}”</p>}
              <div className="admin-lead-contact">
                <a href={`tel:${digits}`} data-testid={`lead-call-${index}`}><Phone size={13} /> {lead.phone}</a>
                <a href={`mailto:${lead.email}`} data-testid={`lead-email-${index}`}>{lead.email}</a>
                {digits.length >= 10 && <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-${index}`}>WhatsApp <ArrowUpRight size={12} /></a>}
              </div>
            </div>
            <div className="admin-lead-actions">
              {STATUSES.filter((s) => s !== status).map((s) => <button key={s} onClick={() => updateStatus(lead.id, s)} data-testid={`lead-mark-${s.toLowerCase()}-${index}`}>Mark {s}</button>)}
            </div>
          </article>;
        })}
      </div>
    )}
  </div>;
}
